import Bullets from '@atoms/Bullets/Bullets';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { IArticle } from '@models/IArticle';
import ArticleLayoutsEnum from '@models/enums/ArticleLayoutsEnum';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { useState } from 'react';

interface Props extends IArticle {
  readonly layoutType?: ArticleLayoutsEnum;
  readonly isGenreLinkDisabled?: boolean;
  readonly isLightMode?: boolean;
  readonly dataTestId?: string;
}

const ArticleCard = ({
  articleInfo,
  image,
  layoutType,
  readTime,
  title,
  url,
  isGenreLinkDisabled,
  isLightMode,
  dataTestId = 'Article-Card',
}: Props) => {
  const isBelowXXLScreen = useMediaQuery(1920);
  const isBelowXLScreen = useMediaQuery(1440);
  const isMobile = useMediaQuery(768);

  const [isHovered, setIsHovered] = useState(false);
  const { author, publishedDate, genre } = articleInfo ?? {};
  const [firstGenre] = genre ?? [];
  const displayedGenre = isGenreLinkDisabled ? firstGenre?.name : firstGenre;
  const bulletsArray = [displayedGenre, publishedDate, author];

  const titleSize =
    (isBelowXXLScreen && !isBelowXLScreen) || isMobile ? 'h6' : 'h5';

  const imageSize = () => {
    if (layoutType === ArticleLayoutsEnum.THREE) {
      return '(max-width: 768px) 376px, 500px';
    }

    if (
      layoutType === ArticleLayoutsEnum.THREE_TWO_THREE ||
      layoutType === ArticleLayoutsEnum.THREE_TWO_TWO ||
      layoutType === ArticleLayoutsEnum.TWO ||
      layoutType === ArticleLayoutsEnum.TWO_TWO
    ) {
      return '50vw';
    }

    return undefined;
  };

  return (
    <div data-testid={dataTestId}>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative h-full w-full">
          {readTime && (
            <div
              className="cta absolute right-2 bottom-2 z-10 flex items-center gap-2 rounded-full bg-black-100 bg-opacity-50 py-2 pr-5 pl-3 text-lg text-white lg:right-5 lg:bottom-5"
              data-testid="Article-Card-Read-Time"
            >
              <Icon name="clock" colorClassName="text-white" />
              {readTime}
            </div>
          )}

          {url && (
            <a
              href={url}
              rel="noreferrer"
              onClick={() =>
                updateDataLayer({
                  event: 'selectContent',
                  content_type: 'articles_find_out_more',
                  link_text: title,
                })
              }
              data-testid="Article-Card-Link"
            >
              {image && (
                <Image
                  image={image}
                  imageClasses="w-full h-full object-cover left-0 -z-1 aspect-video"
                  sizes={imageSize()}
                  dataTestId="Article-Card-Image"
                />
              )}
            </a>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 px-0 pt-2.5 lg:pt-7">
        <Bullets
          bullets={bulletsArray}
          textSize="body-four"
          {...(isLightMode ? { textColour: 'text-black-100 ' } : {})}
          dataTestId="Article-Card-Info-Bullets"
        />

        {title && (
          <p
            className={classNames(
              titleSize,
              url && isHovered
                ? `text-grey-500 ${isLightMode ? '' : 'dark:text-yellow'}`
                : `text-black-100 ${isLightMode ? '' : 'dark:text-white'}`
            )}
            data-testid="Article-Card-Title"
          >
            {title}
          </p>
        )}
      </div>
    </div>
  );
};

export default ArticleCard;
